import { useDispatch, useSelector } from "react-redux"
import IconBorder from "src/shared/components/data-display/IconBorder/IconBorder"
import Dropdown from "src/shared/components/forms/selelct-dropdown/Dropdown"
import { Modal, ModalProps } from "src/shared/components/layout/modal/Modal"
import useSnackbar from "src/shared/hooks/useSnackbar"
import Icon from "src/shared/icons"
import { toggleRoomDetailsDrawer } from "src/store/navigation/navigationSlice"
import { RootState } from "src/store/store"
import { Controller, useForm } from "react-hook-form"
import InputVerification from "src/shared/components/forms/input-verification/InputVerification"
import { client } from "src/graphql"
import { LOCK_ROOM } from "src/pages/home/graphql/mutations/rooms.mutations"
import { useEffect } from "react"
import { PrimaryButton } from "src/pages/home/room-detail/sections/elements/Elements"
import "./LockRoom.css"
import {
    Estados_Habitaciones,
    useActualizar_Colaboradores_TareasMutation,
    useValidar_Codigo_AutorizacionMutation,
} from "src/gql/schema"
import { REMOVER_RESERVA_HABITACION } from "src/pages/incidencias/incidencias-graphql/mutations/incidencia"
import { useMutation } from "@apollo/client"
import { UareUFingerprintSample } from "src/shared/classes/fingerprint/UareU/interfaces/UareU.interface"
import useFingerprint from "src/shared/hooks/useFingerprint"
import { RoleNames } from "src/shared/hooks/useAuth"
import { useMotivosBloqueoContext } from "src/shared/providers/MotivosBloqueoProvider"
import { useProfile } from "src/shared/hooks/useProfile"
import useLoadingState from "src/shared/hooks/useLoadingState"

const defaultValues = {
    motivo_bloqueo: "",
    codigo: "",
    template_sample: "",
    tipo_bloqueo_id: "",
}

interface LockRoomProps extends ModalProps {
    onConfirmed?: () => void
    habitacion?: any
}

const LockRoom = ({ isOpen, onClose, onConfirmed, habitacion }: LockRoomProps) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
        clearErrors,
        reset,
        getValues,
        watch,
        setValue,
        trigger,
    } = useForm({ defaultValues })

    const { tiposBloqueo } = useMotivosBloqueoContext()
    const { showSnackbar } = useSnackbar()
    const { hotel_id } = useProfile()
    const dispatch = useDispatch()
    const { isLoadingDelayed, toggleIsLoading, tryStartLoading } = useLoadingState()
    const { usuario_id } = useSelector((state: RootState) => state.profile)
    const [removerReserva] = useMutation(REMOVER_RESERVA_HABITACION)

    const template_sample = watch("template_sample")
    const codigo = watch("codigo")
    const [validateCode] = useValidar_Codigo_AutorizacionMutation()
    const [finalizarTarea] = useActualizar_Colaboradores_TareasMutation()

    useEffect(() => {
        if (isOpen) reset({ codigo: "", motivo_bloqueo: "" })
    }, [isOpen])

    const dispatchResults = () => {
        dispatch(toggleRoomDetailsDrawer(false))
        onClose?.()
        onConfirmed?.()
    }

    const onSubmit = async (values: any) => {
        // El candado va antes de validar el codigo: la huella dispara el submit desde un efecto y, si llegaba
        // otra muestra o se daba clic mientras se validaba, se confirmaba dos veces y se duplicaba la incidencia
        if (!tryStartLoading()) {
            return
        }
        try {
            await confirmLock(values)
        } finally {
            toggleIsLoading({ value: false })
        }
    }

    const confirmLock = async (values: any) => {
        const authorizedRoles = [RoleNames.admin, RoleNames.superadmin]

        const data = await validateCode({
            variables: {
                codigo: values.codigo,
                huella: values.template_sample,
            },
        })
        if (
            !authorizedRoles.find((r) => r === data.data?.validar_codigo_huella_autorizacion.usuario?.roles?.[0].nombre)
        ) {
            if (values.codigo) {
                showSnackbar({
                    title: "Código incorrecto",
                    status: "error",
                })
                return
            }
            showSnackbar({
                status: "error",
                title: "Huella inválida",
                text: "¡Ups! no pudimos reconocer tu huella. Por favor, inténtalo nuevamente.",
            })
            return
        }
        if (habitacion?.estado === Estados_Habitaciones.Reservada) {
            await removerReserva({
                variables: {
                    remover_data: {
                        habitacion_id: habitacion?.habitacion_id,
                        usuario_id,
                    },
                },
            })
                .then(() => lock(values))
                .catch((e) => {
                    const message: string = e?.message || ""
                    if (message && message.includes("No se encontro el codigo indicado")) {
                        if (values.codigo) {
                            showSnackbar({
                                title: "Código incorrecto",
                                status: "error",
                            })
                            return
                        }
                        showSnackbar({
                            status: "error",
                            title: "Huella inválida",
                            text: "¡Ups! no pudimos reconocer tu huella. Por favor, inténtalo nuevamente.",
                        })
                        return
                    }
                })
        } else {
            await lock(values)
        }
    }

    const lock = async (values) => {
        try {
            // Si la habitacion trae una tarea en curso -- limpieza, mantenimiento, supervision --
            // el bloqueo tiene que cerrarla junto con el cambio de estado. `bloquear_habitacion`
            // solo mueve el estado, y la tarea quedaba abierta para siempre.
            const colaboradores_tareas_ids =
                habitacion?.colaborador_tareas_sin_finalizar?.map((c: any) => c?.colaborador_tarea_id) || []

            if (colaboradores_tareas_ids.length) {
                const { data: finish } = await finalizarTarea({
                    variables: {
                        datos_tarea: {
                            hotel_id,
                            usuario_id,
                            habitacion_id: habitacion?.habitacion_id,
                            colaboradores_tareas_ids,
                            comentario_estado: values.motivo_bloqueo,
                            estado: Estados_Habitaciones.Bloqueada,
                        },
                    },
                })

                if (finish?.actualizar_colaboradores_tareas) {
                    dispatchResults()
                } else {
                    showSnackbar({
                        title: "Error al bloquear habitación",
                        status: "error",
                    })
                }
                return
            }

            await client.mutate({
                mutation: LOCK_ROOM,
                variables: {
                    codigo: values.codigo,
                    template_sample: values.template_sample,
                    bloquear_habitacion_input: {
                        habitacion_id: habitacion?.habitacion_id,
                        usuario_id,
                        hotel_id,
                        comentario_estado: values.motivo_bloqueo,
                        tipo_bloqueo_id: values.tipo_bloqueo_id,
                    },
                },
            })
            dispatchResults()
        } catch (e) {
            if (values.codigo) {
                showSnackbar({
                    title: "Código incorrecto",
                    status: "error",
                })
                return
            }
            showSnackbar({
                status: "error",
                title: "Huella inválida",
                text: "¡Ups! no pudimos reconocer tu huella. Por favor, inténtalo nuevamente.",
            })
        }
    }

    const onAcquisition = (fingerprint: UareUFingerprintSample) => {
        if (!isOpen) {
            return
        }
        if (!getValues().motivo_bloqueo) {
            showSnackbar({
                status: "error",
                title: "Para continuar selecciona un motivo",
            })
            return
        }
        setValue("template_sample", JSON.parse(fingerprint.samples)[0]?.Data)
    }

    // limpiar codigo al usar huella
    useEffect(() => {
        if (!template_sample) {
            return
        }
        setValue("codigo", "")
        trigger("codigo")
    }, [template_sample])

    // ejecutar el submit al llenar huella con formulario llenado
    useEffect(() => {
        if (template_sample && !codigo) {
            onSubmit(getValues())
        }
    }, [template_sample, codigo])

    const { startAcquisition, stopAcquisition } = useFingerprint({
        client: "UareU",
        onAcquisition,
    })

    useEffect(() => {
        if (isOpen) {
            return startAcquisition()
        }
        return () => {
            stopAcquisition()
        }
    }, [isOpen])

    return (
        <Modal
            className="modal-lock-room"
            withCloseButton
            isOpen={isOpen}
            isCancelableOnClickOutside={false}
            onClose={() => onClose?.()}
        >
            <form className="modal-lock-room__content" onSubmit={handleSubmit(onSubmit)}>
                <div className="modal-lock-room__main">
                    <div className="modal-lock-room__top">
                        <IconBorder
                            secondaryBgColor={"var(--ocupada-card-1)"}
                            secondaryBgDiameter={60}
                            primaryBgColor={"var(--pink-ocupado)"}
                            primaryBgDiameter={30}
                        >
                            <Icon name={"LockFill"} height={18} width={18} color={"var(--white)"}></Icon>
                        </IconBorder>
                        <div className="modal-lock-room__head">
                            <span className="modal-lock-room__title modal-lock-room__text">Bloquear habitación</span>
                            <span className="modal-lock-room__description modal-lock-room__text">
                                {`${habitacion?.tipo_habitacion?.nombre} ${habitacion?.numero_habitacion}`}
                            </span>
                        </div>
                    </div>
                    <Controller
                        control={control}
                        name={"tipo_bloqueo_id"}
                        rules={{ required: true }}
                        render={({ field: { value } }) => (
                            <Dropdown
                                icon="userFilled"
                                errorHintText={errors.motivo_bloqueo ? "Selecciona un motivo de bloqueo" : undefined}
                                value={value}
                                label="Motivo de bloqueo"
                                placeholder="Selecciona una opción"
                                onClick={(value) => {
                                    setValue("motivo_bloqueo", value.label)
                                    setValue("tipo_bloqueo_id", value.value)
                                }}
                                options={tiposBloqueo?.map((m) => {
                                    return {
                                        value: m.tipo_bloqueo_id,
                                        label: m.nombre,
                                    }
                                })}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name={"codigo"}
                        rules={{ required: !template_sample, minLength: 4 }}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <InputVerification
                                className="modal-lock-room__input-veification"
                                label="Código de autorización para bloqueo"
                                value={value}
                                onChange={(value) => {
                                    onChange(value)
                                    setValue("template_sample", "")
                                    if (error) clearErrors("codigo")
                                }}
                                error={!!error}
                                hintText={
                                    error
                                        ? error.type === "minLength"
                                            ? "Escribe el código completo"
                                            : "Escribe el código"
                                        : ""
                                }
                            />
                        )}
                    />
                </div>
                <PrimaryButton text={"Bloquear habitación"} type="submit" disabled={isLoadingDelayed} />
            </form>
        </Modal>
    )
}

export default LockRoom
